import { useRouter } from 'next/router'
import { useEffect, useRef } from 'react'

/**
 * Automatically focuses the main content element after navigation completes.
 *
 * This hook implements WCAG 2.4.1 (Bypass Blocks) by moving focus to the main
 * content area after page navigation, allowing screen reader users to skip
 * repeated navigation elements.
 *
 * Handles both:
 * - Full page navigation (router.push from nav bar)
 * - In-page route changes (router.replace for tab navigation)
 *
 * @param contentRef - A ref to the main content element (typically a heading).
 *                     The element must have tabIndex={-1} to be focusable programmatically.
 *
 * @example
 * ```tsx
 * const MyPage: FC = () => {
 *   const headingRef = useRef<HTMLHeadingElement>(null)
 *   useMainContentFocus(headingRef)
 *
 *   return (
 *     <h1 ref={headingRef} tabIndex={-1} style={{ outline: 'none' }}>
 *       Page Title
 *     </h1>
 *   )
 * }
 * ```
 */
export const useMainContentFocus = (contentRef: React.RefObject<HTMLElement | null>): void => {
    const router = useRouter()
    const previousPath = useRef(router.asPath)
    const initialMount = useRef(true)

    useEffect(() => {
        // Skip focus on initial mount
        if (initialMount.current) {
            initialMount.current = false
            previousPath.current = router.asPath
            return
        }

        // Only focus if path actually changed
        if (previousPath.current !== router.asPath) {
            // Use requestAnimationFrame to ensure DOM is ready
            requestAnimationFrame(() => {
                contentRef.current?.focus()
            })
            previousPath.current = router.asPath
        }
    }, [router.asPath, contentRef])

    // Also handle routeChangeComplete for full page navigations
    useEffect(() => {
        const handleRouteChange = (): void => {
            requestAnimationFrame(() => {
                contentRef.current?.focus()
            })
        }

        router.events.on('routeChangeComplete', handleRouteChange)
        return (): void => {
            router.events.off('routeChangeComplete', handleRouteChange)
        }
    }, [router, contentRef])
}
